import React from 'react';

import {cls} from '../utils';
import {Face, Quat, Vec, dot, normalize, quatTurn, solidFaces, topView} from './dice-geometry';
import {Die, dicePlan} from './dice-roll';

import css from './dice.module.css';

function faceLabel(die: Die, face: Face): string {
	if (die.variant === 'tens') return String((face.number - 1) * 10).padStart(2, '0');
	if (die.variant === 'units') return String(face.number - 1);
	if (die.odd) return face.number === die.odd.face ? String(die.odd.value) : '';
	return String(face.number);
}

const SIZE: Record<number, {radius: number; font: number}> = {
	4: {radius: 46, font: 18},
	6: {radius: 42, font: 24},
	8: {radius: 40, font: 18},
	10: {radius: 38, font: 15},
	12: {radius: 38, font: 15},
	20: {radius: 38, font: 13},
};

const COLOR: Record<number, string> = {
	3: css['die-d3'],
	4: css['die-d4'],
	6: css['die-d6'],
	8: css['die-d8'],
	10: css['die-d10'],
	12: css['die-d12'],
	20: css['die-d20'],
	100: css['die-d100'],
};

const LIGHT = normalize([-0.35, -0.55, 0.75]);

type ChipProps = {
	/** кубик из формулы: d3, d4 … d100 */
	sides: number;
	/** что на нём выпало */
	value: number;
};

/** кость в итогах броска: тот же кубик, каким его видно сверху, с выпавшим числом */
export const DiceChip: React.FC<ChipProps> = ({sides, value}) => {
	const die = dicePlan(sides)[0];
	return (
		<span className={cls(css.chip, COLOR[sides])}>
			{topView(die.solid).map((facet, index) => (
				<span
					key={index}
					className={css.chipFacet}
					style={{
						clipPath: `polygon(${facet.polygon.map(([x, y]) => `${(50 + x * 50).toFixed(1)}% ${(50 + y * 50).toFixed(1)}%`).join()})`,
						filter: `brightness(${(0.55 + 0.5 * facet.light).toFixed(2)})`,
					}}
				/>
			))}
			<span className={css.chipValue}>{value}</span>
		</span>
	);
};

const matrix = (right: Vec, down: Vec, normal: Vec, shift: Vec) =>
	`matrix3d(${[...right, 0, ...down, 0, ...normal, 0, ...shift, 1].map((n) => +n.toFixed(5)).join()})`;

type Props = {
	die: Die;
	/** как кубик повёрнут на столе */
	turn: Quat;
};

export const DiceShape: React.FC<Props> = ({die, turn}) => {
	const {radius, font} = SIZE[die.solid];

	return (
		<div className={cls(css.solid, COLOR[die.sides])}>
			{solidFaces(die.solid).map((face) => {
				const xs = face.polygon.map(([x]) => x * radius);
				const ys = face.polygon.map(([, y]) => y * radius);
				const left = Math.min(...xs);
				const top = Math.min(...ys);
				const shift = [0, 1, 2].map((i) => face.center[i] * radius + left * face.right[i] + top * face.down[i]) as Vec;
				const light = Math.max(0, dot(quatTurn(turn, face.normal), LIGHT));
				const label = faceLabel(die, face);
				return (
					<div
						key={face.number}
						className={css.face}
						style={{
							width: Math.max(...xs) - left,
							height: Math.max(...ys) - top,
							clipPath: `polygon(${xs.map((x, i) => `${(x - left).toFixed(2)}px ${(ys[i] - top).toFixed(2)}px`).join()})`,
							transform: matrix(face.right, face.down, face.normal, shift),
							filter: `brightness(${(0.5 + 0.65 * light).toFixed(3)})`,
						}}
					>
						<span
							className={cls(css.faceLabel, die.solid > 6 && /^[69]$/.test(label) && css.underline)}
							style={{left: -left, top: -top, fontSize: font}}
						>
							{label}
						</span>
					</div>
				);
			})}
		</div>
	);
};
